import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { TypedOrder } from '@lds-efsp/shared-contracts';
import { useBrand } from '../../../app/config/brand';
import { useApi, API_BASE_URL } from '../../../api/useApi';
import { StatusBadge } from '../components/StatusBadge';
import { PageTitle } from '../../../shared/components/ui/PageTitle';
import { Card } from '../../../shared/components/ui/Card';

const ORDER_TYPE_LABELS: Record<string, string> = {
  'e-filing': 'eFiling',
  'court-filing': 'Court Filing',
  'process-serving': 'Process Serving',
  'document-retrieval': 'Document Retrieval',
  'courtesy-copy-delivery': 'Courtesy Copy Delivery',
  'secretary-of-state-filing': 'Secretary of State Filing',
  'county-recording': 'County Recording',
  'skip-trace': 'Skip Trace',
  'eservice': 'eService',
};

const STATUS_DISPLAY: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  'in-progress': 'In Progress',
  'pending-review': 'Pending Review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

interface OrdersResponse {
  data: TypedOrder[];
  pagination: { page: number; per_page: number; total: number };
}

const CustomerOrders: React.FC = () => {
  const brand = useBrand();
  const navigate = useNavigate();
  const { get } = useApi();

  const [orders, setOrders] = useState<TypedOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  /* eslint-disable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect -- loading flag before async fetch */
  useEffect(() => {
    setLoading(true);
    get<OrdersResponse>(`${API_BASE_URL}/api/orders?perPage=50`)
      .then((res) => {
        setOrders(res.data);
        setTotal(res.pagination.total);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [get]);
  /* eslint-enable @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect */

  return (
    <div>
      <PageTitle>Orders</PageTitle>

      {loading && (
        <p className="text-sm py-8 text-center" style={{ color: brand.colors.textLight }}>
          Loading orders...
        </p>
      )}

      {error && (
        <Card className="mb-4">
          <p className="text-sm text-red-500">Failed to load orders: {error}</p>
        </Card>
      )}

      {!loading && !error && orders.length === 0 && (
        <div className="text-center py-12">
          <h3 className="text-xl font-light mb-2" style={{ color: brand.colors.textLight }}>
            There are no orders in your history.
          </h3>
          <p className="text-sm" style={{ color: brand.colors.textMuted }}>
            Orders you place will be available here.
          </p>
        </div>
      )}

      {!loading && orders.length > 0 && (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm" style={{ color: brand.colors.textLight }}>
              {total} order{total !== 1 ? 's' : ''}
            </p>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b" style={{ borderColor: brand.colors.border }}>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Order #</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Type</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Case</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Status</th>
                <th className="text-left text-xs font-medium py-2 pr-4" style={{ color: brand.colors.textLight }}>Submitted</th>
                <th className="text-right text-xs font-medium py-2" style={{ color: brand.colors.textLight }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => (
                <tr
                  key={order.id}
                  className="border-b cursor-pointer hover:bg-gray-50"
                  style={{ borderColor: brand.colors.border }}
                  onClick={() => navigate(`/orders/${order.id}`)}
                >
                  <td className="py-3 pr-4">
                    <span className="text-sm font-medium" style={{ color: brand.colors.link }}>
                      {order.referenceNumber}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {ORDER_TYPE_LABELS[order.type] ?? order.type}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.text }}>
                      {order.caseTitle || order.caseNumber || '\u2014'}
                    </span>
                  </td>
                  <td className="py-3 pr-4">
                    <StatusBadge status={STATUS_DISPLAY[order.status] || order.status} brand={brand} />
                  </td>
                  <td className="py-3 pr-4">
                    <span className="text-sm" style={{ color: brand.colors.textLight }}>
                      {order.submittedAt
                        ? new Date(order.submittedAt).toLocaleString()
                        : new Date(order.createdAt).toLocaleString()}
                    </span>
                  </td>
                  <td className="py-3 text-right">
                    <span className="text-sm font-medium" style={{ color: brand.colors.text }}>
                      {order.totalAmount != null ? `$${order.totalAmount.toFixed(2)}` : '\u2014'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};

export default CustomerOrders;
